import React, { useContext, useState } from 'react';
import moment from 'moment-timezone';
import pearClient from 'apis/pearClient';
import {
  VisitType,
  PatientOverviewDto,
  TreatmentProgressSummaryDto,
} from '@peartherapeutics/hylia-api-client';
import getHyliaApiClient from 'clients/hylia-api';
import { DATE_FORMATS, shiftDate, unshiftDate } from 'utils/dateTime';
import ButtonSmall from 'components/common/Buttons/ButtonSmall';
import ErrorInterceptor from 'components/common/ErrorInterceptor';
import { DashboardContext } from 'components/screens/PatientDashboard/context';
import VisitModal from './VisitModal';
import { CLINIC_VISIT_DIALOG_TRACKING_ACTIONS, getWarningType } from './utils';
import styles from './styles.module.scss';

interface IState {
  selectedAppointmentDate?: Date;
  showVisitModal: boolean;
}

export interface ILastClinicVisitProps {
  treatmentSummary: TreatmentProgressSummaryDto;
  setTreatmentSummary: (tps: TreatmentProgressSummaryDto | undefined) => void;
  setOverview: (o: PatientOverviewDto | undefined) => void;
}

const LastClinicVisit = ({
  treatmentSummary,
  setTreatmentSummary,
  setOverview,
}: ILastClinicVisitProps) => {
  const [state, setState] = useState<IState>({
    showVisitModal: false,
    selectedAppointmentDate: undefined,
  });
  const { showVisitModal, selectedAppointmentDate } = state;
  const { selectedAccessRight, selectedArId, patientId } =
    useContext(DashboardContext);
  const lastClinicVisitDate = treatmentSummary.lastClinicVisit
    ? moment(treatmentSummary.lastClinicVisit).tz(selectedAccessRight!.timezone)
    : undefined;

  const handleCloseModal = ({ isSubmitted }: { isSubmitted: boolean }) => {
    if (!isSubmitted) {
      pearClient.v2.tracking.trackVisitedClinicDialogEvent(
        patientId!,
        getWarningType(lastClinicVisitDate),
        CLINIC_VISIT_DIALOG_TRACKING_ACTIONS.DISMISSED,
      );
    }

    setState({
      showVisitModal: false,
      selectedAppointmentDate,
    });
  };

  const updateSelectedAppointmentDate = (date: Date): void => {
    setState({
      showVisitModal,
      selectedAppointmentDate: date,
    });
  };

  const updateAppointmentCompliance = async () => {
    if (selectedAppointmentDate) {
      try {
        const { startDate, endDate, timezone } = selectedAccessRight!;
        const adjustedDate = unshiftDate(
          new Date(selectedAppointmentDate),
          timezone,
        );
        const selected = moment(adjustedDate).tz(timezone);
        const arStart = moment(startDate).tz(timezone);
        const arEnd = moment(endDate).tz(timezone);
        const today = moment().tz(timezone);
        let date = selected.toISOString();
        if (selected.isSame(arStart, 'date')) {
          date = arStart.toISOString();
        } else if (selected.isSame(arEnd, 'date')) {
          date = arEnd.toISOString();
        } else if (selected.isSame(today, 'date')) {
          date = today.toISOString();
        }
        const apiClient = await getHyliaApiClient();
        await apiClient.createPatientAppointmentCompliance(
          patientId!,
          selectedArId!,
          { date, type: VisitType.Attended },
        );

        await pearClient.v2.tracking.trackVisitedClinicDialogEvent(
          patientId!,
          getWarningType(lastClinicVisitDate),
          CLINIC_VISIT_DIALOG_TRACKING_ACTIONS.SUBMITTED,
        );
        const newAppointmentCompliances = [
          ...treatmentSummary!.appointmentCompliances,
          {
            date,
            type: VisitType.Attended,
          },
        ];
        newAppointmentCompliances.sort((a, b) =>
          moment(a.date).diff(moment(b.date)),
        );
        setTreatmentSummary({
          ...treatmentSummary!,
          appointmentCompliances: newAppointmentCompliances,
          lastClinicVisit:
            newAppointmentCompliances[newAppointmentCompliances.length - 1]
              .date,
        });
        setOverview(undefined);
      } catch (e) {
        ErrorInterceptor.error(e);
      }
    }
  };

  const onAddVisit = () => {
    const visitExistsForToday = treatmentSummary.appointmentCompliances.some(
      (appointment) =>
        moment(appointment.date)
          .tz(selectedAccessRight!.timezone)
          .isSame(moment().tz(selectedAccessRight!.timezone), 'date'),
    );
    const initialSelectedDate = visitExistsForToday
      ? null
      : shiftDate(new Date(), selectedAccessRight!.timezone);
    setState({
      showVisitModal: true,
      selectedAppointmentDate: initialSelectedDate ?? undefined,
    });
    pearClient.v2.tracking.trackVisitedClinicDialogEvent(
      patientId!,
      getWarningType(lastClinicVisitDate),
      CLINIC_VISIT_DIALOG_TRACKING_ACTIONS.SHOWN,
    );
  };

  function getDaysSinceClinicVisitText(): string {
    if (lastClinicVisitDate) {
      const diffDays = moment()
        .tz(selectedAccessRight!.timezone)
        .diff(lastClinicVisitDate.tz(selectedAccessRight!.timezone), 'days');
      return `${diffDays} ${diffDays === 1 ? 'day' : 'days'} ago`;
    }
    return '';
  }

  function getLastClinicVisitText(): string {
    return treatmentSummary.lastClinicVisit
      ? moment(treatmentSummary.lastClinicVisit)
          .tz(selectedAccessRight!.timezone)
          .format(DATE_FORMATS.M_D_YYYY)
      : 'Not Entered';
  }

  return (
    <div className={styles.wrapper} data-testid="last-clinic-visit">
      <div className={styles.info}>
        <div className={styles.title} data-testid="last-clinic-visit-text">
          Last Clinic Visit: {getDaysSinceClinicVisitText()}
        </div>
        <p className={styles.description} data-testid="last-clinic-visit-date">
          {getLastClinicVisitText()}
        </p>
        <br />
        <ButtonSmall
          buttonRole="dashboard"
          handleClick={onAddVisit}
          data-testid="log-visit-button"
        >
          Log Visit
        </ButtonSmall>
      </div>
      <VisitModal
        appointmentCompliances={treatmentSummary.appointmentCompliances}
        arStartDate={selectedAccessRight!.startDate}
        arTimezone={selectedAccessRight!.timezone}
        showVisitModal={showVisitModal}
        selectedAppointmentDate={selectedAppointmentDate}
        updateAppointmentCompliance={updateAppointmentCompliance}
        updateSelectedAppointmentDate={updateSelectedAppointmentDate}
        handleCloseModal={handleCloseModal}
      />
    </div>
  );
};

export default LastClinicVisit;
